import * as React from "react";
import {
    Box,
    Chip,
    FormControl,
    Grid,
    Input,
    InputLabel,
    MenuItem,
    Select,
} from "@material-ui/core";
import { LoadingSpinner } from "../../../../../../utils/LoadingSpinner";
import { API } from "aws-amplify";
import ErrorResponse from "./ErrorResponse";

interface IProps {
  personId?: string;
  // When provided, this prescription is auto-selected once the customer's
  // prescriptions load (used to preselect the medication carried from the NewRx
  // ingestion steps). Only selected if it exists on the account.
  initialPrescriptionId?: string;
}

interface IState {
  exception: boolean;
  message: string;
  prescriptionIds: any[];
  prescriptions: any[];
}

class PrescriptionSection extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      exception: false,
      message: "",
      prescriptionIds: [],
      prescriptions: [],
    };

    this.handlePrescriptions = this.handlePrescriptions.bind(this);
  }

  public render() {
    const showLoadingSpinner = this.state.prescriptions.length === 0;
    const prescriptionNames: Array<{ id: string; name: string }> = [];

    if (this.state.prescriptionIds.length !== 0) {
      for (const prescriptionId of this.state.prescriptionIds) {
        const prescription = this.state.prescriptions.find((obj) => {
          return obj.prescriptionId === prescriptionId;
        });
        prescriptionNames.push({
          id: prescription.prescriptionId,
          name: prescription.description,
        });
      }
    }

    // @ts-ignore
    return (
      <Grid item xs={12}>
        <Grid
          container
          direction="column"
          justify="center"
          alignItems="center"
          spacing={3}
        >
          {showLoadingSpinner && !this.state.exception && <LoadingSpinner />}

          {!showLoadingSpinner && !this.state.exception && (
            <Grid item xs>
              <FormControl>
                <InputLabel id="allergies-mutiple-chip-label">
                  Medications (Required)
                </InputLabel>
                <Select
                  style={{ width: 225 }}
                  labelId="allergies-mutiple-chip-label"
                  id="allergies-mutiple-chip"
                  multiple
                  value={this.state.prescriptionIds}
                  onChange={this.handlePrescriptions}
                  input={<Input id="select-multiple-chip" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap'}}>
                      {(selected as string[]).map((key) => {
                        const prescription = prescriptionNames.find(
                          (i) => i.id === key
                        );
                        if (!prescription) {
                          return null;
                        }
                        return (
                          <Chip
                            key={prescription.name}
                            label={prescription.name}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {this.state.prescriptions.map((prescription) => (
                    <MenuItem
                      key={prescription.description}
                      value={prescription.prescriptionId}
                    >
                      {prescription.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {this.state.message && (
            <ErrorResponse
              message={this.state.message}
              dialogTitle={"Prescription Response Exception"}
            />
          )}
        </Grid>
      </Grid>
    );
  }

  public componentDidMount() {
    this.getPrescriptions();
  }

  public componentDidUpdate(prevProps: IProps) {
    // Re-apply the preselection if the carried prescription changes after the
    // prescriptions have already loaded (robust if this instance is reused).
    if (
      prevProps.initialPrescriptionId !== this.props.initialPrescriptionId &&
      this.state.prescriptions.length > 0
    ) {
      this.applyInitialPrescriptionSelection(this.state.prescriptions);
    }
  }

  // Preselect the carried prescription (the medication uploaded in step 1) when it
  // exists on the account; warn if a value was provided but not found (helps catch
  // handoff bugs during QA).
  private applyInitialPrescriptionSelection(prescriptions: any[]) {
    const { initialPrescriptionId } = this.props;
    if (!initialPrescriptionId) {
      return;
    }
    if (
      prescriptions.some((rx) => rx.prescriptionId === initialPrescriptionId)
    ) {
      this.setState({ prescriptionIds: [initialPrescriptionId] });
    } else {
      console.warn(
        `initialPrescriptionId "${initialPrescriptionId}" was not found in the ` +
          `customer's prescriptions; no medication preselected.`
      );
    }
  }

  public handlePrescriptions(event: React.ChangeEvent<{ value: unknown }>) {
    const prescriptionIds = event.target.value as string[];
    if (prescriptionIds.length === 0) {
      this.setState({ prescriptionIds: [] });
    } else {
      this.setState({ prescriptionIds });
    }
  }

  public getState() {
    return this.state;
  }

  private getPrescriptions() {
    const apiName = "ApaqPortalAPI";
    const path =
      "/order/prescription/get-prescriptions?personId=" + this.props.personId;
    API.get(apiName, path, {})
      .then((value: any) => {
        const result = [];
        for (const entry of value.prescriptions) {
          result.push(entry);
        }

        this.setState({ prescriptions: result });

        this.applyInitialPrescriptionSelection(result);

        if (result.length === 0) {
          this.setState({
            message: "No medication found on customer account.",
          });
          this.setState({ exception: true });
        }
      })
      .catch((error) => {
        console.error(error);
        this.setState({ message: error.response.data.message });
        this.setState({ exception: true });
      });
  }
}

export default PrescriptionSection;
